import { useRef, useState, useMemo } from 'react';
import { Redirect, useHistory, useRouteMatch, useLocation } from 'react-router';
import { Switch, matchPath } from 'react-router-dom';
import { MembersTable } from './table/MembersTable';
import { PageNotFound } from 'features/main/components/CustomErrorPages';
import { ApmRoute } from '@elastic/apm-rum-react';
import { MemberDetails } from './table/MemberDetails';
import { MemberRemoveConfirmation } from './table/components/MemberRemoveConfirmation';
import { Configuration } from './configuration/Configuration';
import { MembersHeader } from './MembersHeader';
import { Flex } from '@chakra-ui/react';
import { withErrorBoundary } from 'features/common/utils/errorBoundaries';
import { MembersErrorPage } from 'features/main/components/CustomErrorPages/MembersErrorPage';
import { useFeatures } from 'lib/featureFlags/features';

const MembersContainer = () => {
  const { path } = useRouteMatch();
  const history = useHistory();
  const location = useLocation();
  const [, setMutationCompleted] = useState(false);
  const dataTableRef = useRef(null);
  const features = useFeatures();

  const shouldShowHeader = useMemo(() => {
    const currentPath = location.pathname;
    
    // Don't show header if it's a member details page with ID
    const detailsMatch = matchPath(currentPath, {
      path: `${path}/page/:page/details/:id`,
      exact: true
    });
    
    if (detailsMatch) {
      return false;
    }
    
    // Check if we're on configuration route - only show header if feature is enabled, else only PageNotFound should be rendered
    const configurationMatch = matchPath(currentPath, {
      path: `${path}/configuration`,
      exact: true
    });
    
    if (configurationMatch) {
      return features.CONTENT_HUB_TOGGLE;
    }
    
    const showHeaderPatterns = [
      // Any members/page/.... path
      { path: `${path}/page/:page`, exact: false },
      // Base path
      { path: path, exact: true }
    ];
    
    return showHeaderPatterns.some(pattern => 
      matchPath(currentPath, pattern)
    );
  }, [path, location.pathname, features.CONTENT_HUB_TOGGLE]);

  return (
    <>
      <Flex direction='column'>
        {shouldShowHeader && <MembersHeader />}
        <Switch>
          <ApmRoute
            exact
            path={path}
            component={() => (
              <Redirect to={`${path}/page/1${history.location.search}`} />
            )}
          />
          <ApmRoute
            exact
            path={`${path}/page/:page/details/:id`}
            render={(props) => <MemberDetails {...props} />}
          />
          <ApmRoute
            path={`${path}/page/:page`}
            render={(props) => <MembersTable {...props} />}
          />
          <ApmRoute
            exact
            path={`${path}/configuration`}
            render={() =>
              features.CONTENT_HUB_TOGGLE ? <Configuration /> : <PageNotFound />
            }
          />
          <ApmRoute component={PageNotFound} />
        </Switch>
      </Flex>

      <ApmRoute
        exact
        path={`${path}/page/:page/remove/:id`}
        render={(props) => (
          <MemberRemoveConfirmation
            {...props}
            isOpen
            onClose={() => {
              setMutationCompleted(true);
              if (dataTableRef.current as any) {
                (dataTableRef.current as any)?.setPage(1);
              }
              setTimeout(() => {
                history.push(`${path}/page/1${history.location.search}`);
              }, 0);
            }}
          />
        )}
      />
    </>
  );
};

export default withErrorBoundary(MembersContainer, MembersErrorPage);
